import { HomeBulkUpdatePayload } from "../types/bulk-update";
import {
  DevelopmentHome,
  DevelopmentProject,
  DevelopmentProjectHomes,
} from "../types/development-project";
import { Pagination } from "../types/pagination";
import { IDevelopmentProjectService } from "../types/services/development-project-service";
import { FetchResult, GET, PATCH, POST } from "../utils/fetch";

type ProjectsResponse = {
  projects: DevelopmentProject[];
  paginate: Pagination;
};

type UpcomingProjectsResponse = {
  projects: DevelopmentProject[];
};

type ProjectHomesResponse = DevelopmentProjectHomes;

type BulkUpdateResponse = {
  homes: DevelopmentHome[];
};

type ProjectResponse = {
  project: DevelopmentProject;
};

type PropertyResponse = {
  property: DevelopmentHome;
};

function getProjectsQp(
  query?: string,
  location_ids?: string[],
  categories?: string[],
  statuses?: string[],
  budget_ranges?: string[],
  page?: string,
  per_page?: string,
) {
  const qp = new URLSearchParams();

  if (query) {
    qp.set("query", query);
  }
  if (location_ids) {
    location_ids.forEach(value => qp.append("location_ids[]", value));
  }
  if (categories) {
    categories.forEach(value => qp.append("categories[]", value));
  }
  if (statuses) {
    statuses.forEach(value => qp.append("statuses[]", value));
  }
  if (budget_ranges) {
    budget_ranges.forEach(value => qp.append("budget_ranges[]", value));
  }
  if (page) {
    qp.set("page", page);
  }
  if (per_page) {
    qp.set("per_page", per_page);
  }

  return qp;
}

class DevelopmentProjectService implements IDevelopmentProjectService {
  async getProjects(
    query?: string,
    location_ids?: string[],
    categories?: string[],
    statuses?: string[],
    budget_ranges?: string[],
    page?: string,
    per_page?: string,
  ): Promise<FetchResult<DevelopmentProject[]>> {
    const qp = getProjectsQp(
        query,
        location_ids,
        categories,
        statuses,
        budget_ranges,
        page,
        per_page,
      ),
      marshalled = qp.toString(),
      url = marshalled ?
        `/api/v2/development/projects?${marshalled}`
        :
        "/api/v2/development/projects",
      { response, error } = await GET<ProjectsResponse>(url);

    if (error) {
      return { response: null, error, paginate: undefined };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
        paginate: undefined,
      };
    }

    return { response: response.projects, error: null, paginate: response.paginate };
  }

  async getUpcomingProjects(): Promise<FetchResult<DevelopmentProject[]>> {
    const url = "/api/v2/development/projects/upcoming",
      { response, error } = await GET<UpcomingProjectsResponse>(url);

    if (error) {
      return { response: null, error };
    }

    if (!response) {
      return { response: null, error: new Error("Unknown error") };
    }

    return { response: response.projects, error: null };
  }

  async getProjectHomes(
    id: number,
    opportunitySlug?: string,
    categories?: string[],
    statuses?: string[],
    budgetRanges?: string[],
    query?: string,
  ): Promise<FetchResult<DevelopmentProjectHomes>> {
    const qp = new URLSearchParams();

    if (opportunitySlug) {
      qp.set("opportunity_slug", opportunitySlug);
    }
    if (categories) {
      categories.forEach(value => qp.append("categories[]", value));
    }
    if (statuses) {
      statuses.forEach(value => qp.append("statuses[]", value));
    }
    if (budgetRanges) {
      budgetRanges.forEach(value => qp.append("budget_ranges[]", value));
    }
    if (query) {
      qp.set("query", query);
    }

    const marshalled = qp.toString(),
      url = marshalled ?
        `/api/v2/development/projects/${id}/homes?${marshalled}`
        :
        `/api/v2/development/projects/${id}/homes`,
      { response, error } = await GET<ProjectHomesResponse>(url);

    if (error) {
      return { response: null, error };
    }

    if (!response) {
      return { response: null, error: new Error("Unknown error") };
    }

    return { response, error: null };
  }

  async bulkUpdateHomes(
    projectId: number,
    homes: HomeBulkUpdatePayload[],
  ): Promise<FetchResult<DevelopmentHome[]>> {
    const url = `/api/v2/development/projects/${projectId}/homes/bulk_update`,
      { response, error } = await PATCH<BulkUpdateResponse>(url, { homes });

    if (error) {
      return { response: null, error };
    }

    if (!response) {
      return { response: null, error: new Error("Unknown error") };
    }

    return { response: response.homes, error: null };
  }

  getCsvTemplateUrl(projectId: number): string {
    return `/api/v2/development/projects/${projectId}/csv_template`;
  }

  async uploadCsv(
    projectId: number,
    file: File,
  ): Promise<FetchResult<{ message: string }>> {
    const url = `/api/v2/development/projects/${projectId}/csv_upload`,
      formData = new FormData();

    formData.append("file", file);

    const { response, error } = await POST<{ message: string }>(url, formData);

    if (error) {
      return { response: null, error };
    }

    if (!response) {
      return { response: null, error: new Error("Unknown error") };
    }

    return { response, error: null };
  }

  async activateProject(projectId: number): Promise<FetchResult<DevelopmentProject>> {
    const url = `/api/v2/development/projects/${projectId}/activate`,
      { response, error } = await PATCH<ProjectResponse>(url, {});

    if (error) {
      return { response: null, error };
    }

    if (!response) {
      return { response: null, error: new Error("Unknown error") };
    }

    return { response: response.project, error: null };
  }

  async deactivateProject(projectId: number): Promise<FetchResult<DevelopmentProject>> {
    const url = `/api/v2/development/projects/${projectId}/deactivate`,
      { response, error } = await PATCH<ProjectResponse>(url, {});

    if (error) {
      return { response: null, error };
    }

    if (!response) {
      return { response: null, error: new Error("Unknown error") };
    }

    return { response: response.project, error: null };
  }

  async activateHome(propertyId: number): Promise<FetchResult<DevelopmentHome>> {
    const url = `/api/v2/development/properties/${propertyId}/activate`,
      { response, error } = await PATCH<PropertyResponse>(url, {});

    if (error) {
      return { response: null, error };
    }

    if (!response) {
      return { response: null, error: new Error("Unknown error") };
    }

    return { response: response.property, error: null };
  }
}

export default DevelopmentProjectService;
